import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Ship, LayoutDashboard, FolderKanban, Factory, LogOut, Menu, X, Columns3, Building2, GitBranch, Package, Users, Shield, Anchor, Truck, CalendarDays, ChevronDown, ChevronRight, Eye, Settings, ClipboardList, Database, ShoppingCart, ScrollText, UserCircle2, Target, FileText, CheckSquare, BarChart3, Award, Bell, MessageCircle, ShieldCheck, Activity, FileBarChart, LifeBuoy } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AlertasDropdown from './AlertasDropdown';
import VersionBadge from './VersionBadge';
import { NauFloatingButton } from './NauFloatingButton';

const Layout = ({ children, setIsAuthenticated }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    visao: true,
    gestao: true,
    crm: false,
    cadastros: false,
    configuracoes: false
  });
  const user = JSON.parse(localStorage.getItem('nautilus_user') || '{}');

  const handleLogout = async () => {
    const token = localStorage.getItem('nautilus_token');
    if (token) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        // Proceed even if backend is unreachable
      }
    }
    localStorage.removeItem('nautilus_token');
    localStorage.removeItem('nautilus_user');
    setIsAuthenticated(false);
    toast.success('Logout realizado com sucesso');
    navigate('/login');
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Menu organizado em grupos
  const menuGroups = [
    {
      key: 'visao',
      label: 'Visão Geral',
      icon: Eye,
      items: [
        { path: '/', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/kanban', label: 'Kanban', icon: Columns3 },
        { path: '/nau', label: 'Nau IA', icon: Anchor },
      ]
    },
    {
      key: 'gestao',
      label: 'Gestão de Projetos',
      icon: ClipboardList,
      items: [
        { path: '/projects', label: 'Projetos', icon: FolderKanban },
        { path: '/cronograma', label: 'Cronograma', icon: CalendarDays },
        { path: '/production', label: 'Produção', icon: Factory },
        { path: '/compras', label: 'Compras', icon: ShoppingCart },
      ]
    },
    {
      key: 'crm',
      label: 'CRM',
      icon: UserCircle2,
      items: [
        { path: '/crm/dashboard', label: 'Dashboard', icon: BarChart3 },
        { path: '/crm/pipeline', label: 'Pipeline', icon: Target },
        { path: '/crm/propostas', label: 'Propostas', icon: FileText },
        { path: '/crm/tarefas', label: 'Tarefas', icon: CheckSquare },
        { path: '/crm/clientes', label: 'Clientes', icon: Users },
        { path: '/whatsapp', label: 'WhatsApp', icon: MessageCircle },
      ]
    },
    {
      key: 'relatorios',
      label: 'Relatórios',
      icon: ClipboardList,
      path: '/relatorios',
    },
    {
      key: 'cadastros',
      label: 'Cadastros',
      icon: Database,
      items: [
        { path: '/factories', label: 'Fábricas', icon: Factory },
        { path: '/fornecedores', label: 'Fornecedores', icon: Truck },
        { path: '/produtos', label: 'Produtos & Estoque', icon: Package },
        { path: '/vessel-models', label: 'Modelos', icon: Anchor },
        { path: '/processes', label: 'Processos', icon: GitBranch },
      ]
    },
    {
      key: 'configuracoes',
      label: 'Configurações',
      icon: Settings,
      items: [
        { path: '/empresas', label: 'Empresas', icon: Building2 },
        { path: '/configuracoes/supplier-score', label: 'Supplier Score', icon: Award },
        { path: '/configuracoes/alertas', label: 'Alertas CRM', icon: Bell },
        { path: '/configuracoes/whatsapp', label: 'WhatsApp (Meta)', icon: MessageCircle },
        { path: '/configuracoes/zapi', label: 'Z-API (Alertas)', icon: MessageCircle },
        { path: '/profiles', label: 'Perfis de Acesso', icon: Shield },
        { path: '/users', label: 'Usuários', icon: Users },
        // Backup e Logs - apenas para admin
        ...(user.role === 'admin' ? [
          { path: '/backup', label: 'Backup', icon: Database },
          { path: '/logs', label: 'Logs de Auditoria', icon: ScrollText },
          { path: '/configuracoes/auditoria', label: 'Auditoria', icon: ShieldCheck },
          { path: '/configuracoes/status', label: 'Status do Sistema', icon: Activity },
          { path: '/configuracoes/sla', label: 'SLA & Monitoramento', icon: FileBarChart },
          { path: '/configuracoes/chamados', label: 'Chamados', icon: LifeBuoy },
        ] : []),
      ]
    }
  ];

  // Verificar se algum item do grupo está ativo
  const isGroupActive = (group) => {
    return group.items.some(item => location.pathname === item.path);
  };

  // Renderizar item de menu
  const renderMenuItem = (item, isMobile = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => isMobile && setMobileMenuOpen(false)}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
          isActive
            ? 'bg-blue-600 text-white'
            : 'text-blue-100 hover:bg-blue-700 hover:text-white'
        }`}
      >
        <Icon size={18} />
        <span className="font-medium text-sm">{item.label}</span>
      </Link>
    );
  };

  // Renderizar grupo de menu
  const renderMenuGroup = (group, isMobile = false) => {
    const GroupIcon = group.icon;
    const isExpanded = expandedGroups[group.key];
    const hasActiveItem = isGroupActive(group);
    
    return (
      <div key={group.key} className="mb-2">
        {/* Header do grupo */}
        <button
          onClick={() => toggleGroup(group.key)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
            hasActiveItem 
              ? 'bg-blue-700/50 text-white' 
              : 'text-blue-200 hover:bg-blue-700/30 hover:text-white'
          }`}
        >
          <div className="flex items-center space-x-3">
            <GroupIcon size={18} />
            <span className="font-semibold text-sm">{group.label}</span>
          </div>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        
        {/* Itens do grupo */}
        {isExpanded && (
          <div className="mt-1 ml-3 pl-3 border-l border-blue-700 space-y-1">
            {group.items.map(item => renderMenuItem(item, isMobile))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex print:!block" data-testid="layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-gradient-to-b from-blue-900 to-blue-800 text-white print:!hidden">
        <div className="p-6 border-b border-blue-700">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <Ship size={32} />
            </div>
            <div>
              <h1 className="text-xl font-bold">Nautilus OS</h1>
              <div className="flex items-center gap-2">
                <p className="text-xs text-blue-200">Enterprise</p>
                <VersionBadge />
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuGroups.map(group => 
            group.path 
              ? renderMenuItem(group)
              : renderMenuGroup(group)
          )}
        </nav>

        <div className="p-4 border-t border-blue-700">
          <div className="bg-blue-700 rounded-lg p-4 mb-3">
            <p className="text-sm font-medium">{user.full_name}</p>
            <p className="text-xs text-blue-200">{user.email}</p>
            <p className="text-xs text-blue-300 mt-1 capitalize">{user.role}</p>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="ghost"
            className="w-full text-blue-100 hover:bg-blue-700 hover:text-white"
          >
            <LogOut className="mr-2" size={20} />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-blue-900 text-white z-50 print:!hidden">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <Ship size={24} />
            <span className="font-bold">Nautilus OS</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertasDropdown />
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-blue-900 text-white z-40 pt-16 overflow-y-auto print:!hidden">
          <nav className="p-4 space-y-1">
            {menuGroups.map(group => 
              group.path 
                ? renderMenuItem(group, true)
                : renderMenuGroup(group, true)
            )}
          </nav>

          <div className="p-4 border-t border-blue-700 mt-4">
            <div className="bg-blue-700 rounded-lg p-4 mb-3">
              <p className="text-sm font-medium">{user.full_name}</p>
              <p className="text-xs text-blue-200">{user.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full text-blue-100 hover:bg-blue-700 hover:text-white"
            >
              <LogOut className="mr-2" size={20} />
              Sair
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 bg-gray-50 overflow-auto flex flex-col print:!ml-0 print:!p-0 print:!bg-white">
        {/* Top Bar with Alerts - Desktop */}
        <div className="hidden lg:flex items-center justify-end p-4 bg-white border-b shrink-0 print:!hidden">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Olá, {user.full_name || 'Usuário'}
            </span>
            <AlertasDropdown />
          </div>
        </div>
        {/* Page Content */}
        <div className="flex-1 p-6 lg:p-8 pt-20 lg:pt-6 overflow-auto print:!p-0 print:!pt-0">
          {children}
        </div>
      </main>
      <NauFloatingButton />
    </div>
  );
};

export default Layout;
